import compile from "idyll-compiler";
import IdyllDocument from "idyll-document";
import * as components from "idyll-components";
import Head from "next/head";
import { useState, useRef } from "react";

import Layout from "./layout.js";
import Root, { Controls } from "../components/Root.js";

import "../styles.scss";

const Footnote = ({ children }) => {
  const [isVisible, setVisible] = useState(false);
  const marginNoteNode = useRef(null);

  return (
    <span className="MarginNoteContainer">
      <span
        className="MarginNoteMarker"
        onClick={() => {
          let dismiss;
          dismiss = event => {
            if (
              (event.target && marginNoteNode && marginNoteNode.current.contains(event.target)) ||
              (typeof TouchEvent !== "undefined" &&
                event instanceof TouchEvent &&
                marginNoteNode.current.contains(event.touches[0].target))
            ) {
              return;
            }

            setVisible(false);
            event.stopPropagation();
            document.removeEventListener("touchstart", dismiss);
            document.removeEventListener("click", dismiss, { capture: true });
          };
          document.addEventListener("touchstart", dismiss);
          document.addEventListener("click", dismiss, {
            capture: true,
          });
          setVisible(true);
        }}
      />
      <span ref={marginNoteNode} className={`MarginNote ${isVisible ? "Visible" : ""}`}>
        {children}
      </span>
    </span>
  );
};

const meta = {
  books: {
    metadata: <>
      <meta
        name="citation_title"
        content="Why books don't work"
      />
      <meta name="citation_author" content="Matuschak, Andy" />
      <meta name="citation_publication_date" content="2019" />
      <meta
        name="citation_fulltext_html_url"
        content="https://andymatuschak.org/books"
      />
      <meta name="citation_fulltext_world_readable" content="" />

      <meta property="og:url" content="https://andymatuschak.org/books" />
      <meta property="og:type" content="article" />
      <meta
        property="og:title"
        content="Why books don't work"
      />
      <meta
        property="og:description"
        content="Designing media to reflect how people think and learn"
      />
      <meta
        property="og:image"
        content="https://andymatuschak.org/static/images/social/books.png"
      />
      <meta
        property="og:image:alt"
        content="Title rendered in color: 'Why books don't work'"
      />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1024" />
      <meta property="og:image:height" content="512" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        content="Why books don't work"
      />
      <meta
        name="twitter:image"
        content="https://andymatuschak.org/static/images/social/books.png"
      />
      <meta
        name="twitter:image:alt"
        content="Title rendered in color: 'Why books don't work'"
      />
      <meta
        name="twitter:description"
        content="Designing media to reflect how people think and learn"
      />
    </>,
    title: `Why books donʼt work`
  },

  "doing-centric": {
    metadata: <>
    <meta
      name="citation_title"
      content="Doing-centric explanatory mediums: board game instruction manuals and an unusual Figma document"
    />
    <meta name="citation_author" content="Matuschak, Andy" />
    <meta name="citation_publication_date" content="2022" />
    <meta
      name="citation_fulltext_html_url"
      content="https://andymatuschak.org/doing-centric"
    />
    <meta name="citation_fulltext_world_readable" content="" />

    <meta property="og:url" content="https://andymatuschak.org/doing-centric" />
    <meta property="og:type" content="article" />
    <meta
      property="og:title"
      content="Doing-centric explanatory mediums: board game instruction manuals and an unusual Figma document"
    />
    <meta
      property="og:image"
      content="https://andymatuschak.org/static/doing-centric/figma1.png"
    />
    <meta name="twitter:card" content="summary_large_image" />
    <meta
        name="twitter:image"
        content="https://andymatuschak.org/static/doing-centric/figma1.png"
      />
      <meta
        name="twitter:image:alt"
        content="Figma document demonstrating inline integration of explanation and doing"
      />
  </>,
    title: `Doing-centric explanatory mediums: board game instruction manuals and an unusual Figma document`
  },

  2020: {
    metadata: <>
      <meta
        name="citation_title"
        content="Reflections on 2020 as an independent researcher"
      />
      <meta name="citation_author" content="Matuschak, Andy" />
      <meta name="citation_publication_date" content="2020" />
      <meta
        name="citation_fulltext_html_url"
        content="https://andymatuschak.org/2020"
      />
      <meta name="citation_fulltext_world_readable" content="" />

      <meta property="og:url" content="https://andymatuschak.org/2020" />
      <meta property="og:type" content="article" />
      <meta
        property="og:title"
        content="Reflections on 2020 as an independent researcher"
      />
    </>,
    title: `Reflections on 2020 as an independent researcher`
  },

  2021: {
    metadata: <>
      <meta
        name="citation_title"
        content="Lessons from 2021"
      />
      <meta name="citation_author" content="Matuschak, Andy" />
      <meta name="citation_publication_date" content="2022" />
      <meta
        name="citation_fulltext_html_url"
        content="https://andymatuschak.org/2021"
      />
      <meta name="citation_fulltext_world_readable" content="" />

      <meta property="og:url" content="https://andymatuschak.org/2021" />
      <meta property="og:type" content="article" />
      <meta
        property="og:title"
        content="Lessons from 2021"
      />
    </>,
    title: `Lessons from 2021`
  },

  2022: {
    metadata: <>
      <meta
        name="citation_title"
        content="Three years of crowdfunded research"
      />
      <meta name="citation_author" content="Matuschak, Andy" />
      <meta name="citation_publication_date" content="2023" />
      <meta
        name="citation_fulltext_html_url"
        content="https://andymatuschak.org/2022"
      />
      <meta name="citation_fulltext_world_readable" content="" />

      <meta property="og:url" content="https://andymatuschak.org/2022" />
      <meta property="og:type" content="article" />
      <meta
        property="og:title"
        content="Three years of crowdfunded research"
      />
    </>,
    title: `Three years of crowdfunded research`
  },

  2023: {
    metadata: <>
      <meta
        name="citation_title"
        content="In praise of the particular, and other lessons from 2023"
      />
      <meta name="citation_author" content="Matuschak, Andy" />
      <meta name="citation_publication_date" content="2024" />
      <meta
        name="citation_fulltext_html_url"
        content="https://andymatuschak.org/2023"
      />
      <meta name="citation_fulltext_world_readable" content="" />

      <meta property="og:url" content="https://andymatuschak.org/2023" />
      <meta property="og:type" content="article" />
      <meta
        property="og:title"
        content="In praise of the particular, and other lessons from 2023"
      />
    </>,
    title: `In praise of the particular, and other lessons from 2023`
  },

  "impact-through-intimacy": {
    metadata: <>
      <meta
        name="citation_title"
        content="Towards impact through intimacy in my memory system research"
      />
      <meta name="citation_author" content="Matuschak, Andy" />
      <meta name="citation_publication_date" content="2022" />
      <meta
        name="citation_fulltext_html_url"
        content="https://andymatuschak.org/impact-through-intimacy"
      />
      <meta name="citation_fulltext_world_readable" content="" />

      <meta property="og:url" content="https://andymatuschak.org/impact-through-intimacy" />
      <meta property="og:type" content="article" />
      <meta
        property="og:title"
        content="Towards impact through intimacy in my memory system research"
      />
    </>,
    title: "Towards impact through intimacy in my memory system research"
  },

  sdac: {
    metadata: <>
      <meta
        name="citation_title"
        content="Tools for thought: science, design, art, craftsmanship?"
      />
      <meta name="citation_author" content="Matuschak, Andy" />
      <meta name="citation_publication_date" content="2022" />
      <meta
        name="citation_fulltext_html_url"
        content="https://andymatuschak.org/sdac"
      />
      <meta name="citation_fulltext_world_readable" content="" />

      <meta property="og:url" content="https://andymatuschak.org/sdac" />
      <meta property="og:type" content="article" />
      <meta
        property="og:title"
        content="Tools for thought: science, design, art, craftsmanship?"
      />
    </>,
    title: `Tools for thought: science, design, art, craftsmanship?`
  },

  "sight-reading": {
    metadata: <>
      <meta
        name="citation_title"
        content="Implicit practice: a sight reading parable"
      />
      <meta name="citation_author" content="Matuschak, Andy" />
      <meta name="citation_publication_date" content="2022" />
      <meta
        name="citation_fulltext_html_url"
        content="https://andymatuschak.org/sight-reading"
      />
      <meta name="citation_fulltext_world_readable" content="" />

      <meta property="og:url" content="https://andymatuschak.org/sight-reading" />
      <meta property="og:type" content="article" />
      <meta
        property="og:title"
        content="Implicit practice: a sight reading parable"
      />
    </>,
    title: `Implicit practice: a sight reading parable`
  },

  "stillness": {
    metadata: <>
      <meta
        name="citation_title"
        content="Cultivating depth and stillness in research"
      />
      <meta name="citation_author" content="Matuschak, Andy" />
      <meta name="citation_publication_date" content="2022" />
      <meta
        name="citation_fulltext_html_url"
        content="https://andymatuschak.org/stillness"
      />
      <meta name="citation_fulltext_world_readable" content="" />

      <meta property="og:url" content="https://andymatuschak.org/stillness" />
      <meta property="og:type" content="article" />
      <meta
        property="og:title"
        content="Cultivating depth and stillness in research"
      />
    </>,
    title: `Cultivating depth and stillness in research`
  },

  "personal-ai-ethics": {
    metadata: <>
      <meta
        name="citation_title"
        content="Ethics of AI-based invention: a personal inquiry"
      />
      <meta name="citation_author" content="Matuschak, Andy" />
      <meta name="citation_publication_date" content="2022" />
      <meta
        name="citation_fulltext_html_url"
        content="https://andymatuschak.org/personal-ai-ethics"
      />
      <meta name="citation_fulltext_world_readable" content="" />

      <meta property="og:url" content="https://andymatuschak.org/personal-ai-ethics" />
      <meta property="og:type" content="article" />
      <meta
        property="og:title"
        content="Ethics of AI-based invention: a personal inquiry"
      />
    </>,
    title: "Ethics of AI-based invention: a personal inquiry"
  },

  primer: {
    metadata: <>
      <meta
        name="citation_title"
        content="Exorcising us of the Primer"
      />
      <meta name="citation_author" content="Matuschak, Andy" />
      <meta name="citation_publication_date" content="2024" />
      <meta
        name="citation_fulltext_html_url"
        content="https://andymatuschak.org/primer"
      />
      <meta name="citation_fulltext_world_readable" content="" />

      <meta property="og:url" content="https://andymatuschak.org/primer" />
      <meta property="og:type" content="article" />
      <meta
        property="og:title"
        content="Exorcising us of the Primer"
      />
    </>,
    title: `Exorcising us of the Primer`
  },


  "woz-learning": {
    metadata: <>
      <meta
        name="citation_title"
        content="Becoming a Wizard-of-Oz learning assistant"
      />
      <meta name="citation_author" content="Matuschak, Andy" />
      <meta name="citation_publication_date" content="2023" />
      <meta
        name="citation_fulltext_html_url"
        content="https://andymatuschak.org/woz-learning"
      />
      <meta name="citation_fulltext_world_readable" content="" />

      <meta property="og:url" content="https://andymatuschak.org/woz-learning" />
      <meta property="og:type" content="article" />
      <meta
        property="og:title"
        content="Becoming a Wizard-of-Oz learning assistant"
      />
    </>,
    title: `Becoming a Wizard-of-Oz learning assistant`
  },

  "worth-learning-agi": {
    metadata: <>
      <meta
        name="citation_title"
        content="What's worth learning if we have AGI?"
      />
      <meta name="citation_author" content="Matuschak, Andy" />
      <meta name="citation_publication_date" content="2024" />
      <meta
        name="citation_fulltext_html_url"
        content="https://andymatuschak.org/worth-learning-agi"
      />
      <meta name="citation_fulltext_world_readable" content="" />

      <meta property="og:url" content="https://andymatuschak.org/worth-learning-agi" />
      <meta property="og:type" content="article" />
      <meta
        property="og:title"
        content="What's worth learning if we have AGI?"
      />
    </>,
    title: `What\u2019s worth learning if we have AGI?`
  },


};

const Hr = () => <hr />

const MailingListInterstitial = () => (<div className="MailingListInterstitialContainer">
  <Controls />
</div>
)

const availableComponents = { ...components, Footnote, Hr, MailingListInterstitial };

export default class Post extends React.Component {
  static async getInitialProps({ query }) {
    const markup = await require(`../content/${query.post}.md`);
    const ast = await compile(markup.default);
    return { ast, slug: query.post };
  }

  render() {
    return (
      <Layout>

        <Head>
          <title>{meta[this.props.slug].title} | Andy Matuschak</title>
          {meta[this.props.slug].metadata}
        </Head>

        <div className="SidebarContainer">
          <Root sidebar />
          <div className="NotSidebar">
            <div className="wideOnly">
              <Controls />
            </div>
          </div>
        </div>
        <div className="NarrowHeader narrowOnly">
          <div className="NameAndButtons">
            <h1>
              <a href="/">Andy Matuschak</a>
            </h1>
            <div className="HeaderButtons narrowOnly">
              <a href="mailto:andy@andymatuschak.org">Email</a>
              <a href="https://twitter.com/andy_matuschak">Twitter</a>
            </div>
          </div>
        </div>
        <div className="NotSidebar" style={{ flexGrow: 1 }}>
          <div className="PostContainer">
            <article>
              <header>
                <h1>{meta[this.props.slug].title}</h1>
              </header>
              <div className="PostBodyContainer">
                <IdyllDocument
                  ast={this.props.ast}
                  components={availableComponents}
                  datasets={{}}
                />
              </div>
            </article>
          </div>
        </div>
      </Layout>
    );
  }
}
